import { Vector2 } from "../../common/vector2.js";
import { Game } from "../../game.js";
import { ImgID } from "../../image_loader/img_id.js";
import { StatisticsStorage } from "../../storage/statistics_storage.js";
import { TransformScale } from "../../transforms/transform_scale.js";
import { TransformStraightMove } from "../../transforms/transform_straight_move.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../common_ui_elements/ui_element.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";
import { TelegramAPISettings } from "../../telegram_api_settings.js";

export class NavigationItem extends UIElement
{
  private img_id: ImgID;
  private text: string;

  private is_calendar: boolean;

  private image_el!: UIImage;
  private text_el!: UITextElement;
  private msg_el?: UIImage;
  private img_bg_el?: UIImage;

  private readonly ITEM_MAIN_SCALE = 1.5;
  private readonly ITEM_Y_UP = 75;
  private readonly MOBILE_DEFAULT_ITEM_DOWN = 15;

  private default_img_y!: number;
  private default_text_y!: number;

  private main_img_y!: number;
  private main_text_y!: number;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    img_id: ImgID,
    text: string,
    is_calendar: boolean
  )
  {
    super( ctx, x, y, width, height, false, false );

    this.img_id = img_id;
    this.text = text;

    this.is_calendar = is_calendar;

    this.createContent();
  }

  private createContent(): void
  {
    const is_telegram = TelegramAPISettings.isTelegramEnvironment();
    const mobile_default_down = !is_telegram && Game.isMobile()
      ? LayoutScaler.yFromDesign( this.MOBILE_DEFAULT_ITEM_DOWN )
      : 0;
    const item_down = LayoutScaler.yFromDesign( is_telegram ? 55 : 0 )
      + mobile_default_down;
    const bg_w = LayoutScaler.widthFromDesign( 180 );
    const bg_h = LayoutScaler.heightFromDesign( 180 );
    const bg_x = this.rect.getW2() - bg_w / 2;
    const bg_y = this.rect.getH2() - bg_h / 2
      + LayoutScaler.yFromDesign( 25 )
      + item_down;

    const bg = new UIImage(
      this.ctx,
      bg_x,
      bg_y,
      bg_w,
      bg_h,
      ImgID.UI_NavigationIconBg,
      {}
    );
    this.img_bg_el = bg;

    this.addChild( bg );
    bg.setClickable( false );

    const w = LayoutScaler.widthFromDesign( 145 );
    const h = LayoutScaler.heightFromDesign( 145 );
    const text_h = LayoutScaler.heightFromDesign( 70 );

    const x = this.rect.getW2() - w / 2;
    const y = this.rect.getH2() - h / 2
      + LayoutScaler.yFromDesign( 25 )
      + item_down;

    const img = new UIImage(
      this.ctx,
      x,
      y,
      w,
      h,
      this.img_id,
      {}
    )

    this.default_img_y = y;
    this.default_text_y = y + h + LayoutScaler.heightFromDesign( 20 );

    const item_y_up = this.ITEM_Y_UP;

    this.main_img_y =
      this.default_img_y - LayoutScaler.yFromDesign( item_y_up ) - mobile_default_down;

    this.main_text_y =
      this.default_text_y - LayoutScaler.yFromDesign( item_y_up / 2 ) - mobile_default_down;

    const text = new UITextElement(
      this.ctx,
      x - w / 2,
      y + h + LayoutScaler.heightFromDesign( 20 ),
      w * 2,
      text_h,
      {
        localization_key: this.text,
        vertical_align: MyTextVerticalAlign.Top,
        text_align: MyTextAlign.Center,
        text_color: ColorScheme.white,
        font_size: LayoutScaler.textSizeFromDesign( 35 ),
        font_weight: 900,
        text_style: TextStyleScheme.geologica,
        auto_wrap: false,
        outline_color: ColorScheme.navigation_text_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        outline_offset_y: LayoutScaler.yFromDesign( 1 ),
        shadow_blur: LayoutScaler.widthFromDesign( 4 ),
        shadow_offset_y: LayoutScaler.heightFromDesign( 6 ),
        shadow_color: ColorScheme.black_transparent
      }
    );

    img.setClickable( false );
    text.setClickable( false );

    text.hideElement( 0 );

    this.addChild( img );
    this.addChild( text );

    this.image_el = img;
    this.text_el = text;
  }

  public hideMsgIcon(): void
  {
    this.msg_el && this.removeChild( this.msg_el );
  }

  public doMain( is_init = false ): void
  {
    const img = this.image_el;
    const text = this.text_el;

    this.img_bg_el?.hideElement( is_init ? 0 : 150 );
    this.text_el.showElement( is_init ? 0 : 150 );

    const scale_count = this.ITEM_MAIN_SCALE;

    const scale = new TransformScale(
      img,
      true,
      null,
      new Vector2( scale_count, scale_count ),
      performance.now(),
      is_init ? 0 : 150
    );

    const move_to = new TransformStraightMove(
      img,
      true,
      null,
      new Vector2(
        img.pixelPosForRender().x,
        this.main_img_y
      ),
      performance.now(),
      is_init ? 0 : 150
    )

    const move_to_text = new TransformStraightMove(
      text,
      true,
      null,
      new Vector2(
        text.pixelPosForRender().x,
        this.main_text_y
      ),
      performance.now(),
      is_init ? 0 : 150
    )

    const text_scale = new TransformScale(
      text,
      true,
      null,
      new Vector2( scale_count, scale_count ),
      performance.now(),
      is_init ? 0 : 150
    );

    img.addTransform( scale );
    img.addTransform( move_to );

    text.addTransform( text_scale );
    text.addTransform( move_to_text );
  }

  public doDefault(): void
  {
    const img = this.image_el;
    const text = this.text_el;

    this.img_bg_el?.showElement( 120 );
    this.text_el.hideElement( 120 );

    const scale = new TransformScale(
      img,
      true,
      null,
      new Vector2( 1, 1 ),
      performance.now(),
      150
    );

    const move_to = new TransformStraightMove(
      img,
      true,
      null,
      new Vector2(
        img.pixelPosForRender().x,
        this.default_img_y
      ),
      performance.now(),
      150
    )

    const move_to_text = new TransformStraightMove(
      text,
      true,
      null,
      new Vector2(
        text.pixelPosForRender().x,
        this.default_text_y
      ),
      performance.now(),
      150
    )

    const text_scale = new TransformScale(
      text,
      true,
      null,
      new Vector2( 1, 1 ),
      performance.now(),
      150
    );

    img.addTransform( scale );
    img.addTransform( move_to );

    text.addTransform( text_scale );
    text.addTransform( move_to_text );
  }

  public name(): string
  {
    return "NavigationItem";
  }

  public update( now: number ): void
  {
    super.updateBasic( now );
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
  }
}
